import './settings/settings-leaf.css';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { deleteAccount } from '../api/account.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { ACCOUNT_DELETION_MEMBER_CONFIRMATION, SUPPORT_EMAIL } from '@shared/ai-disclosure.ts';
import { Alert, Button, TextInput } from './ui.tsx';

/**
 * Settings → Delete account.
 *
 * An owner types the company name and deletes the company. Anyone else types
 * DELETE and removes only their user. The server decides which, from the
 * company role; this form only chooses the words that match that decision.
 */
export default function DeleteAccountSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, company, clearSession } = useSession();
  const owner = user?.role === 'owner';
  const expected = owner ? (company?.name ?? '') : ACCOUNT_DELETION_MEMBER_CONFIRMATION;
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      await deleteAccount(confirmation.trim());
      clearSession();
      navigate('/delete-account', { replace: true });
    } catch (caught) {
      setBusy(false);
      setError(caught instanceof ApiRequestError ? caught.message : t('settings.delete.failed'));
    }
  }

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="delete-account-heading">
      <div className="work-section-heading">
        <h2 id="delete-account-heading" className="mb-3 text-lg font-semibold">
          {t('settings.delete.heading')}
        </h2>
      </div>
      <Alert tone="warning" title={t('settings.delete.warningTitle')}>
        {owner ? t('settings.delete.ownerWarning') : t('settings.delete.memberWarning')}
      </Alert>
      <p className="mb-4 mt-4 text-sm text-[color:var(--color-muted)]">
        {t('settings.delete.retention', { email: SUPPORT_EMAIL })}
      </p>
      <form onSubmit={(event) => void handleSubmit(event)} className="flex flex-col gap-3">
        <TextInput
          label={owner ? t('settings.delete.ownerLabel') : t('settings.delete.memberLabel')}
          hint={owner ? t('settings.delete.ownerHint', { name: expected }) : t('settings.delete.memberHint')}
          value={confirmation}
          autoComplete="off"
          onChange={(event) => setConfirmation(event.target.value)}
        />
        {error ? <Alert tone="error">{error}</Alert> : null}
        <div>
          <Button type="submit" variant="danger" busy={busy} disabled={confirmation.trim() !== expected.trim()}>
            {t('settings.delete.submit')}
          </Button>
        </div>
      </form>
    </section>
  );
}
